import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    createInvoiceTemplateBodySchema,
    createInvoiceTemplateFromInvoiceBodySchema,
    invoiceTemplateContentBodySchema,
    invoiceTemplateDetailSchema,
    invoiceTemplateListItemSchema,
    invoiceTemplateMetadataSchema,
    listInvoiceTemplatesQuerySchema,
    renameInvoiceTemplateBodySchema,
} from "@common/schemas/domains/invoice/invoice-template.schema";

const c = initContract();

const InvoiceTemplateEntityPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    entityId: z.coerce.number().int(),
});

const InvoiceTemplateFromInvoicePathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    entityId: z.coerce.number().int(),
    invoiceId: z.string().uuid(),
});

const InvoiceTemplatePathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    entityId: z.coerce.number().int(),
    templateId: z.string().uuid(),
});

export const invoiceTemplateContract = c.router(
    {
        createInvoiceTemplate: {
            method: "POST",
            path: "/:workspaceId/:entityId/templates",
            pathParams: InvoiceTemplateEntityPathParamsSchema,
            body: createInvoiceTemplateBodySchema,
            responses: {
                201: invoiceTemplateDetailSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        createInvoiceTemplateFromInvoice: {
            method: "POST",
            path: "/:workspaceId/:entityId/templates/from-invoice/:invoiceId",
            pathParams: InvoiceTemplateFromInvoicePathParamsSchema,
            body: createInvoiceTemplateFromInvoiceBodySchema,
            responses: {
                201: invoiceTemplateDetailSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        listInvoiceTemplates: {
            method: "GET",
            path: "/:workspaceId/:entityId/templates",
            pathParams: InvoiceTemplateEntityPathParamsSchema,
            query: listInvoiceTemplatesQuerySchema,
            responses: {
                // `total`/`pageCount` are only meaningful when `page`/`limit` were
                // passed in the query; unpaginated callers can ignore them (they
                // reflect the full matching set either way). Mirrors
                // catalogItemContract.listCatalogItems.
                200: z.object({
                    data: z.array(invoiceTemplateListItemSchema),
                    total: z.number(),
                    pageCount: z.number(),
                }),
                // The query schema rejects an invalid page/limit (out of the
                // both-or-nothing pair, or limit above the max) with a 400.
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getInvoiceTemplate: {
            method: "GET",
            path: "/:workspaceId/:entityId/templates/:templateId",
            pathParams: InvoiceTemplatePathParamsSchema,
            responses: {
                200: invoiceTemplateDetailSchema,
                404: ErrorMessageSchema,
            },
        },
        renameInvoiceTemplate: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/templates/:templateId",
            pathParams: InvoiceTemplatePathParamsSchema,
            body: renameInvoiceTemplateBodySchema,
            responses: {
                200: invoiceTemplateMetadataSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        archiveInvoiceTemplate: {
            method: "POST",
            path: "/:workspaceId/:entityId/templates/:templateId/archive",
            pathParams: InvoiceTemplatePathParamsSchema,
            body: z.object({}),
            responses: {
                200: invoiceTemplateMetadataSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unarchiveInvoiceTemplate: {
            method: "POST",
            path: "/:workspaceId/:entityId/templates/:templateId/unarchive",
            pathParams: InvoiceTemplatePathParamsSchema,
            body: z.object({}),
            responses: {
                200: invoiceTemplateMetadataSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        deleteInvoiceTemplate: {
            method: "DELETE",
            path: "/:workspaceId/:entityId/templates/:templateId",
            pathParams: InvoiceTemplatePathParamsSchema,
            responses: {
                204: z.void(),
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateInvoiceTemplateContent: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/templates/:templateId/content",
            pathParams: InvoiceTemplatePathParamsSchema,
            body: invoiceTemplateContentBodySchema,
            responses: {
                200: invoiceTemplateDetailSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/invoices",
    },
);
